/**
 * Shared TypeScript types for the Domino Multiplayer game
 * 
 * This file contains types used by both client and server for:
 * - Game logic consistency
 * - WebSocket message protocols  
 * - Data structure definitions
 * - Type safety across the full stack
 */

/**
 * Represents a single domino tile with two values
 * Forms the basic building block of the domino game
 */
export interface DominoTile {
  /** Unique identifier for this specific tile instance */
  id: string;
  /** Value on the left side of the tile (0-6 for standard double-6 set) */
  left: number;
  /** Value on the right side of the tile (0-6 for standard double-6 set) */
  right: number;
}

/**
 * Represents a player participating in the game
 * Contains both game state and connection information
 */
/**
 * DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId for friend system
 */
export interface Player {
  /** Unique player identifier */
  id: string;
  /** Display name for the player */
  name: string;
  /** Tiles currently in the player's hand */
  tiles: DominoTile[];
  /** Whether player is ready to start the game */
  isReady: boolean;
  /** Whether it's currently this player's turn */
  isCurrentTurn: boolean;
  /** Current score (used for blocked game resolution) */
  score: number;
  /** Whether player is currently connected via WebSocket */
  connected: boolean;
  /** Whether this player is controlled by AI (replaces disconnected or fills empty slots) */
  isAI?: boolean;
  /** NetCartas ID for friend system integration (null if not registered) */
  netcartasId?: number | null;
  /** Profile image URL from NetCartas (optional) */
  profileImageURL?: string | null;
}

/**
 * Represents a tile placed on the board with both game logic and display data
 * 
 * This dual representation allows us to:
 * - Keep original tile data for game logic (validation, scoring, etc.)
 * - Provide properly oriented tile data for visual display
 * - Maintain game history and reconstruction capabilities
 */
export interface PlacedTile {
  /** 
   * Original tile as it exists in the game logic
   * Used for: move validation, scoring, game rules
   * Example: Player's tile might be {left: 0, right: 6}
   */
  tile: DominoTile;
  
  /** 
   * Tile oriented for proper visual display on the board
   * Ensures connecting values are visually adjacent
   * Example: If 0|6 connects to a 6 on the board, this becomes {left: 6, right: 0}
   * so the visual shows [existing|6][6|0] with matching 6s touching
   */
  orientedTile: DominoTile;
  
  /** 
   * ID of the player who played this tile
   * Used for game history, UI highlighting, and rule enforcement
   */
  playerId: string;
  
  /** 
   * Which end of the board this tile was placed on
   * 'left' = placed on the left end of the domino train
   * 'right' = placed on the right end of the domino train
   * Used for board reconstruction and move history
   */
  position: 'left' | 'right';
  
  /** 
   * Timestamp when this tile was played (milliseconds since epoch)
   * Used for move ordering, game history, and debugging
   */
  timestamp: number;
}

/**
 * Possible states of a domino game
 */
export type GameStatus = 'waiting' | 'playing' | 'finished';

/**
 * Game mode: individual (every player for themselves) or partnership (2v2 teams)
 */
export type GameMode = 'individual' | 'partnership';

/**
 * Team assignments for partnership games
 */
export interface TeamAssignment {
  teamA: [string, string]; // Partner IDs (North, South)
  teamB: [string, string]; // Partner IDs (East, West)
}

/**
 * Team scores for partnership games
 */
export interface TeamScores {
  teamA: number;
  teamB: number;
}

/**
 * Resultado de uma rodada completada
 * 
 * DATE: 2025-11-06 | Author: IA + Luiz Couto
 * Armazena informações sobre uma rodada para histórico e análise
 */
export interface RoundResult {
  /** Número da rodada (1, 2, 3, etc) */
  roundNumber: number;
  /** ID do vencedor da rodada (null se empate) */
  winnerId: string | null;
  /** Time vencedor se partnership mode (A ou B) */
  winnerTeam?: 'A' | 'B';
  /** Pontos ganhos nesta rodada */
  pointsEarned: number;
  /** Motivo do término da rodada: 'bateu' (jogou todas as peças) ou 'trancado' (jogo bloqueado) */
  reason: 'bateu' | 'trancado';
  /** Pontos de cada jogador ao final da rodada (para visualização) */
  finalScores: { [playerId: string]: number };
  /** Timestamp quando a rodada terminou */
  timestamp?: number;
}

/**
 * Complete game state for server-side logic
 * Uses Map for efficient player lookups on the server
 * Supports both individual and partnership modes
 */
export interface GameState {
  /** Unique game room identifier */
  id: string;
  /** Current status of the game */
  status: GameStatus;
  /** Map of all players in the game (server uses Map for efficiency) */
  players: Map<string, Player>;
  /** Sequence of tiles placed on the board (ordered from left to right) */
  board: PlacedTile[];
  /** ID of the player whose turn it currently is */
  currentPlayerId: string | null;
  /** Pool of tiles available to draw from */
  boneyard: DominoTile[];
  /** Current value at the left end of the domino train */
  leftEnd: number | null;
  /** Current value at the right end of the domino train */
  rightEnd: number | null;
  /** ID of the winning player (if game is finished) */
  winner: string | null;
  /** Timestamp of last activity (for cleanup/timeouts) */
  lastActivity: number;
  /** When the current turn started (for turn time limits) */
  turnStartTime: number | null;
  /** Number of consecutive passes (for blocked game detection) */
  passCount: number;
  
  // Partnership mode fields (optional - only used when gameMode === 'partnership')
  /** Game mode: individual or partnership */
  gameMode?: GameMode;
  /** Team assignments (only for partnership mode) */
  teams?: TeamAssignment;
  /** Team scores (only for partnership mode) */
  teamScores?: TeamScores;
  /** Turn order for 4-player games [North, East, South, West] */
  turnOrder?: string[];
  
  // DATE: 2025-11-06 | Author: IA + Luiz Couto
  // Novos campos para sistema de rodadas múltiplas
  /** Número da rodada atual (começa em 1) */
  roundNumber?: number;
  /** Meta de pontos para vitória final (ex: 50, 100, 150, 200) */
  pointGoal?: number;
  /** Pontos acumulados por jogador (chave: playerId, valor: pontos) */
  playerScores?: { [playerId: string]: number };
  /** Histórico de rodadas jogadas */
  roundHistory?: RoundResult[];
  /** Primeiro jogador da rodada atual (para rotação) */
  firstPlayerId?: string;
  
  // DATE: 2025-11-07 | Author: IA + Luiz Couto
  // Contador exclusivo de partidas jogadas por IAs (para auto-restart em jogos 100% AI)
  /** Contador de partidas completas jogadas exclusivamente por IAs (cada partida = múltiplas rodadas até meta de 50 pts) */
  gamesPlayedByAICount?: number;
}

/**
 * Client-friendly game state representation
 * Uses plain objects instead of Maps for JSON serialization
 * Supports both individual and partnership modes
 */
export interface ClientGameState {
  /** Unique game room identifier */
  id: string;
  /** Current status of the game */
  status: GameStatus;
  /** Plain object mapping player IDs to player data */
  players: { [key: string]: Player };
  /** Sequence of tiles placed on the board */
  board: PlacedTile[];
  /** ID of the player whose turn it currently is */
  currentPlayerId: string | null;
  /** Number of tiles remaining in the boneyard (not the actual tiles for security) */
  boneyardCount: number;
  /** Current value at the left end of the domino train */
  leftEnd: number | null;
  /** Current value at the right end of the domino train */
  rightEnd: number | null;
  /** ID of the winning player (if game is finished) */
  winner: string | null;
  /** Timestamp of last activity */
  lastActivity: number;
  /** When the current turn started */
  turnStartTime: number | null;
  /** Number of consecutive passes */
  passCount: number;
  
  // Partnership mode fields (optional)
  /** Game mode: individual or partnership */
  gameMode?: GameMode;
  /** Team assignments (only for partnership mode) */
  teams?: TeamAssignment;
  /** Team scores (only for partnership mode) */
  teamScores?: TeamScores;
  /** Turn order for 4-player games */
  turnOrder?: string[];
  
  // DATE: 2025-11-06 | Author: IA + Luiz Couto
  // Sistema de rodadas múltiplas (mesmos campos que GameState)
  /** Número da rodada atual (começa em 1) */
  roundNumber?: number;
  /** Meta de pontos para vitória final (ex: 50, 100, 150, 200) */
  pointGoal?: number;
  /** Pontos acumulados por jogador (chave: playerId, valor: pontos) */
  playerScores?: { [playerId: string]: number };
  /** Histórico de rodadas jogadas */
  roundHistory?: RoundResult[];
  /** Primeiro jogador da rodada atual (para rotação) */
  firstPlayerId?: string;
  
  // DATE: 2025-11-07 | Author: IA + Luiz Couto
  // Contador exclusivo de partidas jogadas por IAs (para auto-restart em jogos 100% AI)
  /** Contador de partidas completas jogadas exclusivamente por IAs (cada partida = múltiplas rodadas até meta de 50 pts) */
  gamesPlayedByAICount?: number;
}

/**
 * Messages sent from client to server via WebSocket
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId to join for friend system
 */
// DATE: 2025-12-21 | Added profileImageURL to join for avatar display
export type ClientMessage =
  | { type: 'join'; name: string; netcartasId?: number | null; profileImageURL?: string | null }
  | { type: 'spectate'; name: string } // 2025-10-28: Join as spectator (read-only)
  | { type: 'ready' }
  | { type: 'play'; tileId: string; position: 'left' | 'right' }
  | { type: 'draw' }
  | { type: 'pass' }
  | { type: 'restart' }
  | { type: 'chat'; message: string };

/**
 * Messages sent from server to client via WebSocket
 */
export type ServerMessage =
  | { type: 'connected'; playerId: string }
  | { type: 'gameState'; state: Partial<ClientGameState> }
  | { type: 'playerJoined'; player: Player }
  | { type: 'playerLeft'; playerId: string }
  | { type: 'gameStarted' }
  | { type: 'tilePlayed'; playerId: string; tile: DominoTile; position: 'left' | 'right' }
  | { type: 'tileDrawn'; playerId: string; tile?: DominoTile }
  | { type: 'turnChanged'; playerId: string }
  | { type: 'gameOver'; winnerId: string; reason: string }
  | { type: 'error'; message: string }
  | { type: 'chat'; playerId: string; playerName: string; message: string };

/**
 * Configuration for the game rules and limits
 */
export interface GameConfig {
  /** Minimum number of players required to start a game */
  minPlayers: number;
  /** Maximum number of players allowed in a game */
  maxPlayers: number;
  /** Number of tiles each player gets at the start */
  tilesPerPlayer: number;
  /** Maximum time allowed per turn (in seconds) */
  turnTimeLimit: number;
  /** Maximum pip value on tiles (6 for double-6, 9 for double-9, etc.) */
  maxTileValue: number;
}

/**
 * Lobby-specific types
 */

// Game type enum - as a real enum (not just export type)
// DATE: 2025-10-31 | Author: IA + Luiz Couto
// Simplified: gameMode determines player count automatically
// - individual_2p (gameMode: 'individual') = 2 players, 1v1
// - partnerships_4p (gameMode: 'partnership') = 4 players, 2v2 teams
export enum DominoGameType {
  INDIVIDUAL_2P = 'individual_2p',      // 1v1 classic dominoes (gameMode: 'individual')
  PARTNERSHIPS_4P = 'partnerships_4p',  // Traditional Brazilian 2v2 teams (gameMode: 'partnership')
  AI_MIXED = 'ai_mixed',                // Humans + AI opponents (future)
  AI_TRAINING = 'ai_training'           // Practice against AI only (future)
}

// Player summary for lobby display
// DATE: 2025-12-17 | Author: IA + Luiz Couto
// Added netcartasId for friend system integration
export interface PlayerSummary {
  id: string;
  name: string;
  connected: boolean;
  isReady: boolean;
  ranking?: number;
  gamesPlayed?: number;
  netcartasId?: number | null; // DATE: 2025-12-17 | NetCartas ID for friend system
}

// Game lobby entry
export interface GameLobbyEntry {
  id: string;
  type: DominoGameType;
  status: GameStatus;
  players: PlayerSummary[];
  maxPlayers: number;
  spectatorCount: number;
  allowSpectators: boolean;
  createdAt: number;
  lastActivity: number;
  description?: string;
  currentPlayerName?: string;
}

// Matchmaking preferences
export interface MatchmakingPreferences {
  gameTypes: DominoGameType[];
  maxPlayers?: number;
  skillLevel?: 'beginner' | 'intermediate' | 'advanced';
  allowAI?: boolean;
  preferredPartner?: string;
  maxWaitTime?: number;
}

// Client-friendly lobby state
export interface ClientLobbyState {
  games: GameLobbyEntry[];
  players: PlayerSummary[];
  matchmakingStatus?: {
    inQueue: boolean;
    queuePosition?: number;
    estimatedWait?: number;
    gameType?: DominoGameType;
  };
  stats: {
    totalActiveGames: number;
    totalActivePlayers: number;
    averageWaitTime: number;
  };
}

// Game configuration
export interface GameConfiguration {
  type: DominoGameType;
  maxPlayers: number;
  isPrivate: boolean;
  allowSpectators: boolean;
  description?: string;
  skillLevel?: 'beginner' | 'intermediate' | 'advanced';
}

// Lobby-specific message types
// DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId to joinLobby for friend system
// DATE: 2025-12-17 | Author: IA + Luiz Couto - Added friend request messages
export type LobbyClientMessage =
  | { type: 'joinLobby'; playerName: string; netcartasId?: number | null }
  | { type: 'getLobbyState' }
  | { type: 'createGame'; config: GameConfiguration }
  | { type: 'joinGame'; gameId: string; role: 'player' | 'spectator' }
  | { type: 'quickMatch'; preferences: MatchmakingPreferences }
  | { type: 'leaveQueue' }
  | { type: 'updatePreferences'; preferences: Partial<any> }
  | { type: 'lobbyChat'; message: string }
  // DATE: 2025-12-17 | Friend request messages
  | { type: 'friendRequest'; targetPlayerId: string; targetNetcartasId: number; saidId: number }
  | { type: 'friendAccept'; fromPlayerId: string; fromNetcartasId: number; saidId: number }
  | { type: 'friendDecline'; fromPlayerId: string; fromNetcartasId: number; saidId: number };

export type LobbyServerMessage =
  | { type: 'lobbyState'; state: ClientLobbyState }
  | { type: 'gameCreated'; gameId: string; redirect: boolean }
  | { type: 'gameJoined'; gameId: string; gameType: DominoGameType }
  | { type: 'matchFound'; gameId: string; gameType: DominoGameType; teammates?: string[] }
  | { type: 'queueUpdate'; position: number; estimatedWait: number }
  | { type: 'gameUpdated'; game: GameLobbyEntry }
  | { type: 'gameRemoved'; gameId: string }
  | { type: 'playerJoinedLobby'; player: PlayerSummary }
  | { type: 'playerLeftLobby'; playerId: string }
  | { type: 'lobbyChat'; playerId: string; playerName: string; message: string }
  | { type: 'error'; message: string }
  // DATE: 2025-12-17 | Friend request notifications
  | { type: 'friendRequestReceived'; fromPlayerId: string; fromPlayerName: string; fromNetcartasId: number; saidId: number; isAI?: boolean }
  | { type: 'friendAccepted'; playerId: string; playerName: string; netcartasId: number }
  | { type: 'friendDeclined'; playerId: string };
